import {Exclude, Expose, instanceToPlain, plainToInstance, Transform} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {Entity} from "./entity";
import {NotificationChannel, toNotificationChannel} from "./notification-channel";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";
import {NotificationProviderType, toNotificationProviderType} from "./notification-provider-type";

export const NotificationSenderProfileCodec = t.intersection([
  t.type({
    id: t.string,
    name: t.string,
    channel: t.string,
    providerType: t.string,
    doNotUse: t.boolean,
    createDttm: t.number,
    lastModifiedDttm: t.number,
    hasApiKeySet: t.boolean,
    hasO365ClientSecret: t.boolean,
    hasSmtpPassword: t.boolean,
    hasTwilioAuthToken: t.boolean,
  }),
  t.partial({
    fromEmail: t.string,
    fromPhoneNumber: t.string,
    fromName: t.string,
    emailReplyTo: t.string,
    emailJavaMailProps: t.string, // JSON
    awsRegion: t.string,
    o365TenantId: t.string,
    o365ClientId: t.string,
    smtpHost: t.string,
    smtpPort: t.number,
    smtpUsername: t.string,
    smtpUseTls: t.boolean,
    smtpUseSsl: t.boolean,
    twilioSid: t.string,
    twilioAuthToken: t.string,
    twilioMessagingServiceSid: t.string,
    createdById: t.string,
    lastModifiedById: t.string,
  }),
]);

export type NotificationSenderProfile = t.TypeOf<typeof NotificationSenderProfileCodec>;

@Exclude()
export class NotificationSenderProfileEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  name!: string;

  @Transform(getDatabaseCodeTransformer(toNotificationChannel))
  @Expose()
  channel!: NotificationChannel;

  @Transform(getDatabaseCodeTransformer(toNotificationProviderType))
  @Expose()
  providerType!: NotificationProviderType;

  @Expose()
  doNotUse!: boolean;

  @Expose()
  createDttm!: number;

  @Expose()
  lastModifiedDttm!: number;

  @Expose()
  hasApiKeySet!: boolean;

  @Expose()
  hasO365ClientSecret!: boolean;

  @Expose()
  hasSmtpPassword!: boolean;

  @Expose()
  hasTwilioAuthToken!: boolean;

  // Optional

  @Expose()
  fromEmail?: string;

  @Expose()
  fromPhoneNumber?: string;

  @Expose()
  fromName?: string;

  @Expose()
  emailReplyTo?: string;

  @Expose()
  emailJavaMailProps?: string;

  @Expose()
  awsRegion?: string;

  @Expose()
  o365TenantId?: string;

  @Expose()
  o365ClientId?: string;

  @Expose()
  smtpHost?: string;

  @Expose()
  smtpPort?: number; // default 587 handled server-side or in constructor if you want

  @Expose()
  smtpUsername?: string;

  @Expose()
  smtpUseTls?: boolean; // default true handled server-side

  @Expose()
  smtpUseSsl?: boolean; // default false handled server-side

  @Expose()
  twilioSid?: string;

  @Expose()
  twilioAuthToken?: string;

  @Expose()
  twilioMessagingServiceSid?: string;

  @Expose()
  createdById?: string;

  @Expose()
  lastModifiedById?: string;

  readonly entityType = 'NOTIF_SEND_PROF';

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for NotificationSenderProfileEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: NotificationSenderProfile): NotificationSenderProfileEntity {
    return plainToInstance(NotificationSenderProfileEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<NotificationSenderProfile> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(NotificationSenderProfileCodec, object);
  }

  asTypeValue(): NotificationSenderProfile {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(NotificationSenderProfileCodec, object);
  }
}

export const NotificationSenderProfileSaveDTOCodec = t.intersection([
  t.type({

  }),
  t.partial({
    id: t.string,
    name: t.string,
    channel: t.string,
    providerType: t.string,
    doNotUse: t.boolean,
    fromEmail: t.string,
    fromPhoneNumber: t.string,
    fromName: t.string,
    emailReplyTo: t.string,
    emailJavaMailProps: t.string, // JSON
    apiKey: t.string,
    awsRegion: t.string,
    o365TenantId: t.string,
    o365ClientId: t.string,
    o365ClientSecret: t.string,
    smtpHost: t.string,
    smtpPort: t.number,
    smtpUsername: t.string,
    smtpPassword: t.string,
    smtpUseTls: t.boolean,
    smtpUseSsl: t.boolean,
    twilioSid: t.string,
    twilioAuthToken: t.string,
    twilioMessagingServiceSid: t.string,
    createdById: t.string,
    lastModifiedById: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
    clearApiKey: t.boolean,
    clearSmtpPassword: t.boolean,
    clearO365ClientSecret: t.boolean,
    clearTwilioAuthToken: t.boolean,
  }),
]);

export type NotificationSenderProfileSaveDTO = t.TypeOf<typeof NotificationSenderProfileSaveDTOCodec>;
